/**
 * The products of the selected stores, searched by name or reference and loaded
 * page by page, with the product, ABC and additional data dialogs. Admin only.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { ROLES } from '@/shared/constants';
import {
  appConfigQueryKey,
  appConfigQueryOptions,
  fetchAppConfig,
  getSelectedStoreIds,
  type StoreId,
} from '@/shared/config/app-config';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SearchForm } from '@/shared/ui/SearchForm';
import plusIcon from '@/assets/images/plus.svg';
import { productPagesQueryOptions, type Product } from './api';
import { productPictureUrl } from './logic';
import { ProductDialog } from './ProductDialog';
import { ProductAbcDialog } from './ProductAbcDialog';
import { AdditionalDataDialog } from './AdditionalDataDialog';

type DialogState =
  | { kind: 'edit'; item: Product | null }
  | { kind: 'abc'; item: Product }
  | { kind: 'data'; item: Product };

export function ProductsPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  // The configuration (product lines) is refreshed when opening the screen.
  useEffect(() => {
    if (!isAdmin) return;
    void fetchAppConfig().then(() =>
      queryClient.invalidateQueries({ queryKey: appConfigQueryKey }),
    );
  }, [isAdmin, queryClient]);

  const { data: config } = useQuery(appConfigQueryOptions());
  const stores = useMemo(() => config?.stores ?? [], [config?.stores]);
  const lines = useMemo(
    () => config?.productLines ?? [],
    [config?.productLines],
  );
  const [selectedIds, setSelectedIds] = useState<StoreId[]>(() =>
    getSelectedStoreIds(),
  );
  const [search, setSearch] = useState('');
  // The search only applies on Enter or on the magnifying glass.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const products = useInfiniteQuery({
    ...productPagesQueryOptions(selectedIds, appliedSearch),
    enabled: isAdmin,
  });
  const items = products.data?.pages.flatMap((page) => page.data) ?? [];
  const nextCursor = products.data?.pages.at(-1)?.nextCursor ?? null;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['products', 'paginate'] });

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    setDialog(null);
    if (result === 'Confirm') void refresh();
  };

  if (!isAdmin) return null;

  return (
    <div className="container summary">
      <TopMenu onStoreSelected={setSelectedIds} />
      <div className="summary-table my-12">
        <div className="mb-4 flex w-full items-center">
          <SearchForm
            style={{ flex: 1 }}
            value={search}
            onChange={setSearch}
            onSubmit={() => setAppliedSearch(search)}
            testId="product-search"
          />
          <button
            type="button"
            className="add-btn flex items-center justify-center"
            data-testid="header-add-product"
            onClick={() => setDialog({ kind: 'edit', item: null })}
          >
            <img src={plusIcon} alt="" />
            <span className="pl-2">Add a product</span>
          </button>
          <div style={{ flex: 1 }} />
        </div>
        <div className="mb-6">
          <div className="table-container">
            <div
              className="items-container"
              role="region"
              aria-label="Products"
            >
              <table className="table items">
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>Reference</th>
                    <th style={{ width: '20%' }}>Name</th>
                    <th style={{ width: '10%' }}>Main picture</th>
                    <th style={{ width: '10%' }}>Central workshop</th>
                    <th style={{ width: '10%' }}>Display order</th>
                    <th style={{ width: '10%' }}>Product line</th>
                    <th style={{ width: '10%' }} className="edit-header">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((product) => (
                    <tr key={product.id} className="product-row">
                      <td
                        style={{ width: '10%' }}
                        title={product.reference ?? ''}
                        data-testid="product-row-reference"
                      >
                        {product.reference}
                      </td>
                      <td
                        style={{ width: '20%' }}
                        title={product.name ?? ''}
                        data-testid="product-row-name"
                      >
                        {product.name}
                      </td>
                      <td style={{ width: '10%' }}>
                        <img
                          className="product-picture"
                          src={
                            productPictureUrl(product.id, 'main') ?? undefined
                          }
                          alt=""
                          data-testid="product-row-main-picture"
                        />
                      </td>
                      <td
                        style={{ width: '10%' }}
                        title={product.workshopName ?? ''}
                        data-testid="product-row-workshop"
                      >
                        {product.workshopName}
                      </td>
                      <td
                        style={{ width: '10%' }}
                        title={String(product.displayOrder ?? '')}
                        data-testid="product-row-display-order"
                      >
                        {product.displayOrder}
                      </td>
                      <td
                        style={{ width: '10%' }}
                        title={product.line ?? ''}
                        data-testid="product-row-line"
                      >
                        {product.line}
                      </td>
                      <td style={{ width: '10%' }} className="edit-action">
                        <div className="flex items-center">
                          <button
                            type="button"
                            className="button"
                            onClick={() =>
                              setDialog({ kind: 'edit', item: product })
                            }
                            data-testid="product-row-edit-button"
                          >
                            edit
                          </button>
                          <button
                            type="button"
                            className="button ml-1 mt-0"
                            onClick={() =>
                              setDialog({ kind: 'abc', item: product })
                            }
                            data-testid="product-row-abc-button"
                          >
                            ABC
                          </button>
                        </div>
                        <div className="mt-1">
                          <button
                            type="button"
                            className="button"
                            onClick={() =>
                              setDialog({ kind: 'data', item: product })
                            }
                            data-testid="product-row-additional-data-button"
                          >
                            Additional Data
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={7} className="py-4! text-center">
                      {(products.isFetching || products.isFetchingNextPage) && (
                        <div className="text-muted">
                          <FontAwesomeIcon icon={faCircleNotch} spin /> Loading
                          more items...
                        </div>
                      )}
                      {nextCursor ? (
                        <button
                          type="button"
                          className="btn btn-link shadow-none"
                          onClick={() => void products.fetchNextPage()}
                          data-testid="product-load-more-button"
                        >
                          Load more items...
                        </button>
                      ) : (
                        <span className="text-muted">No more items</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {dialog?.kind === 'edit' && (
        <ProductDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          stores={stores}
          lines={lines}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'abc' && (
        <ProductAbcDialog
          key={dialog.item.id}
          item={dialog.item}
          lines={lines}
          onClose={(imagesChanged) => {
            setDialog(null);
            if (imagesChanged) void refresh();
          }}
        />
      )}
      {dialog?.kind === 'data' && (
        <AdditionalDataDialog
          key={dialog.item.id}
          item={dialog.item}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
